import * as R from 'ramda';

export const exists = R.complement(R.isNil);


/**
 * The language server incorrectly infers the overload without an explicit type
 * annotation.
 * @type {<V0, V1, T1, T2>(fn1: (x: T1) => T2, fn0: (x0: V0, x1: V1) => T1) => (x0: V0, x1: V1) => T2}
 * */
let compose = R.compose;
export const doesNotEqual = compose(R.not, R.equals);

/**
 * @description Refer to https://github.com/ramda/ramda/blob/master/src/pathOr.js
 * @param {*} defaultValue The default value.
 * @param {Array} path The path to use.
 * @param {Object} object The object to retrieve the nested property from.
 * @return {*} The data at `path` of the supplied object or the default value.
 */
export const pathOr = R.curryN(3, (defaultValue, path, object) =>
    R.defaultTo(defaultValue, R.path(path, object))
);

/**
 * @description Refer to https://github.com/ramda/ramda/blob/master/src/propOr.js
 * @param {*} defaultValue The default value.
 * @param {String} prop The name of the property to return.
 * @param {Object} object The object to query.
 * @return {*} The value of given property of the supplied object or the default value.
 */
export const propOr = R.curryN(3, (defaultValue, prop, object) =>
    object != null && R.has(prop, object) ? object[prop] : defaultValue
);

/**
 * @description Returns a new list without any consecutively repeating elements. Equality is
 * determined by applying the supplied predicate to each pair of consecutive elements. The
 * first element in a series of equal elements will be preserved.
 * @param {Function} pred A predicate used to test whether two items are equal.
 * @param {Array} list The array to consider.
 * @return {Array} `list` without repeating elements.
 */
export const dropRepeatsWith = R.curryN(2, (pred, list) => {
    const result = [];
    let idx = 1;
    const len = list.length;

    if (len !== 0) {
        result[0] = list[0];
        while (idx < len) {
            if (!pred(R.last(result), list[idx])) {
                result[result.length] = list[idx];
            }

            idx += 1;
        }
    }
    return result;
});

/**
 * Creates a new object with the own properties of the provided object, but the
 * keys renamed according to the keysMap object as `{oldKey: newKey}`.
 * When some key is not found in the keysMap, then it's passed as-is.
 *
 * Keep in mind that in the case of keys conflict is behaviour undefined and
 * the result may vary between various JS engines!
 *
 * @sig {a: b} -> {a: *} -> {b: *}
 */
export const renameKeys = R.curry((keysMap, obj) =>
    R.reduce((acc, key) => R.assoc(keysMap[key] || key, obj[key], acc), {}, R.keys(obj))
);

export const eqBy = R.curryN(3, (f, x, y) => R.equals(f(x), f(y)));

export function allObjFieldsExist(obj, fields) {
    if (R.isNil(obj)) {
        return false;
    }

    return R.all(field => exists(obj[field]))(fields);
}

/**
 * @param {Function} func
 * @returns {(obj: any) => any}
 */
export const mapObj = R.curry((func, obj) =>
    R.reduce((acc, /** @type {string} */ key) => R.pipe(R.prop(key), func, R.assoc(key, R.__, acc))(obj), {}, R.keys(obj))
);

/**
 * Nests multiple keys from an object into a new object within the original, placed at the named
 * key and renaming the keys according to the passed map. Example usage:
 *
 * collectUnder('nestedKey', {a: 'e', b: 'f', c: 'g'}, {a: 1, b: 2, c: 3, d: 4});
 * // => {d: 4, nestedKey: {e: 1, f: 2, g: 3}}
 */
export const collectUnder = R.curry((key, keys, obj) => {
    const ks = R.keys(keys);
    // @ts-expect-error
    return R.pipe(R.omit(ks), R.assoc(key, renameKeys(keys, R.pick(ks, obj))))(obj);
});



// WEBPACK FOOTER //
// ./common/object_helper.js